import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import {
  JsonLd,
  organizationSchema,
  softwareApplicationSchema,
  websiteSchema,
} from "@/components/StructuredData";
import { useAppConfig } from "@/lib/use-app-config";
import { Icon } from "@/components/Icon";
import { CarTabIcon } from "@/components/CarImage";

// Root document title mirrors index.html / app/layout.tsx; we still set
// the description here so the homepage's <meta name="description"> reflects
// the marketing copy rather than the index.html fallback.
const TITLE = "Asistent Auto — asistentul tău, atent la detalii";
const DESCRIPTION =
  "Aplicația care îți ține mașinile în ordine: ITP, RCA, CASCO — toate documentele într-un singur loc, cu reminder înainte să expire.";

export default function Home() {
  const config = useAppConfig();
  return (
    <>
      <Seo title={TITLE} description={DESCRIPTION} path="/" />
      <JsonLd
        data={[
          organizationSchema(config),
          websiteSchema(),
          softwareApplicationSchema(),
        ]}
      />
      {/* HERO */}
      <header className="hero">
        <div className="shell hero-inner">
          <div>
            <div className="hero-eyebrow">
              <span className="dot"></span>
              Asistent Auto · Versiunea 1.4
            </div>
            <h1 className="hero-title">
              Asistentul tău,
              <br />
              <em>atent la detalii.</em>
            </h1>
            <p className="hero-sub">
              <strong>Asistent Auto</strong> este aplicația gratuită (Android și
              web) care îți ține evidența documentelor mașinii — ITP, RCA,
              CASCO, rovinietă — îți trimite reminder înainte să expire și te
              lasă să cumperi online asigurarea RCA prin broker autorizat. Nu
              mai uiți. Nu mai cauți.
            </p>
            <div className="hero-actions">
              <a href="#download" className="btn btn--primary btn--lg">
                <Icon name="download" />
                Descarcă gratuit
              </a>
              <Link to="/functii" className="btn btn--secondary btn--lg">
                Vezi cum funcționează
                <Icon name="arrow_forward" />
              </Link>
            </div>
            <div className="hero-meta">
              <span><b>Gratuit</b>, fără reclame</span>
              <span><b>Date locale</b>, criptate</span>
              <span><b>4.5★</b> pe Play Store</span>
            </div>
          </div>

          {/* Phone mock */}
          <div className="phone-stage">
            <div className="phone">
              <div className="phone-notch"></div>
              <div className="phone-screen">
                <div className="mini-statusbar">
                  <span>9:41</span>
                  <span style={{ display: "flex", gap: 4 }}>
                    <Icon name="signal_cellular_alt" style={{ fontSize: 13 }} />
                    <Icon name="wifi" style={{ fontSize: 13 }} />
                    <Icon name="battery_full" style={{ fontSize: 13 }} />
                  </span>
                </div>
                <div className="mini-topbar">
                  <h2>Mașinile mele</h2>
                  <div className="mini-topbar-actions">
                    <button className="mini-icon-btn" type="button">
                      <Icon name="notifications" />
                    </button>
                  </div>
                </div>
                <div className="mini-greeting">
                  Bună, Ana 👋 — ai <b>3 vehicule</b>
                </div>

                {/* Card 1 — OK */}
                <div className="mini-card">
                  <div className="mini-card-hero">
                    <span className="mini-plate">
                      <span className="mini-plate-ro">RO</span>B 101 RYA
                    </span>
                    <span className="mini-status-pill success">
                      <Icon name="check_circle" />
                      Activ
                    </span>
                    <CarTabIcon className="car-glyph" />
                  </div>
                  <div className="mini-card-body">
                    <div className="mini-car-name">BMW X5</div>
                    <div className="mini-car-sub">xDrive40i M Sport · 2023</div>
                    <div className="mini-doc-strip">
                      <div className="mini-doc">
                        <div className="mini-doc-label"><Icon name="shield" />RCA</div>
                        <div className="mini-doc-val success">11 luni</div>
                      </div>
                      <div className="mini-doc">
                        <div className="mini-doc-label"><Icon name="verified_user" />CASCO</div>
                        <div className="mini-doc-val success">8 luni</div>
                      </div>
                      <div className="mini-doc">
                        <div className="mini-doc-label"><Icon name="event_available" />ITP</div>
                        <div className="mini-doc-val success">2026</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card 2 — Warn */}
                <div className="mini-card">
                  <div className="mini-card-hero warm">
                    <span className="mini-plate">
                      <span className="mini-plate-ro">RO</span>CJ 12 ELE
                    </span>
                    <span className="mini-status-pill warn">
                      <Icon name="schedule" />
                      12 zile
                    </span>
                    <CarTabIcon className="car-glyph" />
                  </div>
                  <div className="mini-card-body">
                    <div className="mini-car-name">Dacia Spring</div>
                    <div className="mini-car-sub">Electric 65 · 2024</div>
                  </div>
                </div>

                <div className="mini-bottomnav">
                  <div className="item active">
                    <span className="pill"><CarTabIcon /></span>
                    <span>Mașini</span>
                  </div>
                  <div className="item">
                    <span className="pill"><Icon name="notifications" /></span>
                    <span>Notificări</span>
                  </div>
                  <div className="item">
                    <span className="pill"><Icon name="shopping_bag" /></span>
                    <span>Produse</span>
                  </div>
                  <div className="item">
                    <span className="pill"><Icon name="person" /></span>
                    <span>Cont</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* TRUST STRIP */}
      <section className="section section--tight">
        <div className="shell">
          <div className="trust">
            <div className="trust-broker">
              <div className="trust-broker-eyebrow">
                Reînnoiri prin Inter Broker
              </div>
              <div className="trust-broker-name">Inter Broker</div>
              <div className="trust-broker-meta">
                Broker de asigurare autorizat ASF
              </div>
            </div>
            <div className="trust-via">
              <div className="trust-label">
                Acces la <em>panel de asigurători</em>
              </div>
              <div className="trust-grid">
                <div className="trust-logo">Allianz<small>Țiriac</small></div>
                <div className="trust-logo">Groupama<small>Asigurări</small></div>
                <div className="trust-logo">Omniasig<small>VIG</small></div>
                <div className="trust-logo">Generali<small>România</small></div>
                <div className="trust-logo">Asirom<small>VIG</small></div>
                <div className="trust-logo">Uniqa<small>Asigurări</small></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section">
        <div className="shell">
          <div className="section-eyebrow">Cum funcționează</div>
          <h2 className="section-title">
            Trei pași. <em>Apoi nu mai trebuie să faci nimic.</em>
          </h2>
          <p className="section-lede">
            Configurează o mașină în mai puțin de un minut. Aplicația preia
            restul — reminder la timp, ofertă la nevoie, polița în mână.
          </p>

          <div className="steps">
            <div className="step">
              <div className="step-num">1</div>
              <h3>Adaugi mașina</h3>
              <p>
                Scanezi talonul cu camera și câmpurile se completează singure.
                Pentru completare, datele tehnice se aduc din DRPCIV după VIN
                sau plăcuță.
              </p>
              <div className="step-visual">
                <span
                  className="mini-plate"
                  style={{ fontSize: 14, padding: "8px 14px" }}
                >
                  <span
                    className="mini-plate-ro"
                    style={{ width: 16, height: 16, fontSize: 9 }}
                  >
                    RO
                  </span>
                  B 101 RYA
                </span>
              </div>
            </div>

            <div className="step">
              <div className="step-num">2</div>
              <h3>Verifici online expirarea</h3>
              <p>
                Pentru ITP, RCA și Rovinieta — un tap și aplicația interoghează
                RAR, aida.info.ro și CNAIR. Data exactă de expirare apare automat
                în calendar, fără să tastezi nimic.
              </p>
              <div className="step-visual">
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <span className="pill success">
                    <Icon name="verified" />
                    ITP · RAR
                  </span>
                  <span className="pill success">
                    <Icon name="verified" />
                    RCA · aida
                  </span>
                  <span className="pill success">
                    <Icon name="verified" />
                    Rovinieta
                  </span>
                </div>
              </div>
            </div>

            <div className="step">
              <div className="step-num">3</div>
              <h3>Primești reminder calm</h3>
              <p>
                30 zile, 7 zile, ziua dinainte. Notificare push sau email — tu
                alegi. Dacă e RCA, deschizi aplicația și ai deja oferte
                comparate prin Inter Broker.
              </p>
              <div className="step-visual" style={{ padding: 14 }}>
                <div
                  className="reminder"
                  style={{ width: "100%", boxShadow: "none" }}
                >
                  <div className="icon-box">
                    <Icon name="schedule" />
                  </div>
                  <div className="copy">
                    <strong>ITP — Dacia Spring</strong>
                    <span>Expiră peste 12 zile</span>
                  </div>
                  <div className="when">azi · 09:00</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KEY FEATURES — BENTO */}
      <section className="section">
        <div className="shell">
          <div className="section-eyebrow">Ce face mai bine</div>
          <h2 className="section-title">
            Trei lucruri pe care le facem <em>obsesiv bine.</em>
          </h2>

          <div className="bento">
            {/* Tile 1: Reminders */}
            <div className="tile span-8">
              <div className="icon-box">
                <Icon name="schedule" />
              </div>
              <h3>Reminder-uri care ajung exact la timp</h3>
              <p>
                Nu trei zile mai târziu, când ai și o amendă. Aplicația te
                avertizează din timp pentru ITP, RCA, CASCO, rovinietă și
                vinietă.
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  marginTop: 24,
                }}
              >
                <div className="reminder">
                  <div className="icon-box">
                    <Icon name="shield" />
                  </div>
                  <div className="copy">
                    <strong>RCA — VW Golf 8</strong>
                    <span>Mâine. Compară 8 oferte cu un tap.</span>
                  </div>
                  <div className="when">mâine · 08:00</div>
                </div>
                <div className="reminder">
                  <div
                    className="icon-box"
                    style={{
                      background: "var(--success-container)",
                      color: "var(--success)",
                    }}
                  >
                    <Icon name="event_available" />
                  </div>
                  <div className="copy">
                    <strong>ITP — BMW X5</strong>
                    <span>
                      Termen confortabil. Te anunțăm peste 60 de zile.
                    </span>
                  </div>
                  <div className="when">2026</div>
                </div>
              </div>
            </div>

            {/* Tile 2: Multi-vehicle */}
            <div className="tile span-4">
              <div className="icon-box">
                <Icon name="groups" />
              </div>
              <h3>Hub pentru toată familia</h3>
              <p>
                Adaugi mașina partenerului, a părinților, a flotei mici. Fiecare
                cu propriul status — fără să te încurci.
              </p>
              <div
                style={{
                  marginTop: "auto",
                  paddingTop: 24,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {[
                  { plate: "B 101 RYA", text: "BMW X5 · Ana" },
                  { plate: "CJ 12 ELE", text: "Spring · Mihai" },
                  { plate: "B 33 GTI", text: "Golf 8 · Ana" },
                ].map((v) => (
                  <div
                    key={v.plate}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      fontSize: 13,
                    }}
                  >
                    <span className="mini-plate" style={{ fontSize: 11 }}>
                      <span className="mini-plate-ro">RO</span>
                      {v.plate}
                    </span>
                    <span style={{ color: "var(--on-surface-variant)" }}>
                      {v.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tile: Online expiration check */}
            <div className="tile span-12 online-check">
              <div className="online-check-copy">
                <div className="icon-box">
                  <Icon name="fact_check" />
                </div>
                <h3>
                  Verifici online <em>ITP, RCA și Rovinieta.</em>
                </h3>
                <p>
                  Introduci numărul mașinii sau VIN-ul. Aplicația interoghează
                  sursele oficiale și completează data exactă de expirare în
                  calendar — fără să tastezi nimic.
                </p>
                <div className="online-check-sources">
                  <span className="source-chip">
                    RAR <small>itp</small>
                  </span>
                  <span className="source-chip">
                    aida.info.ro <small>rca</small>
                  </span>
                  <span className="source-chip">
                    CNAIR <small>rovinieta</small>
                  </span>
                </div>
              </div>
              <div className="online-check-demo">
                <div className="check-card">
                  <div className="check-card-head">
                    <span className="mini-plate">
                      <span className="mini-plate-ro">RO</span>B 101 RYA
                    </span>
                    <span className="pill info">
                      <Icon name="cloud_done" />
                      Verificat acum
                    </span>
                  </div>
                  <div className="check-row">
                    <div className="check-row-label">
                      <Icon name="event_available" />
                      <strong>ITP</strong>
                      <span className="src">RAR</span>
                    </div>
                    <div className="check-date-val">14.03.2026</div>
                  </div>
                  <div className="check-row">
                    <div className="check-row-label">
                      <Icon name="shield" />
                      <strong>RCA</strong>
                      <span className="src">aida</span>
                    </div>
                    <div className="check-date-val">28.11.2026</div>
                  </div>
                  <div className="check-row warn">
                    <div className="check-row-label">
                      <Icon name="toll" />
                      <strong>Rovinieta</strong>
                      <span className="src">CNAIR</span>
                    </div>
                    <div className="check-date-val">
                      07.05.2026
                      <span className="check-date-rel">9 zile</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tile 3: Compare offers */}
            <div className="tile span-12 compare-tile">
              <div className="compare-tile-grid">
                <div className="compare-tile-copy">
                  <div className="icon-box">
                    <Icon name="compare_arrows" />
                  </div>
                  <h3>Compară oferte RCA în 12 secunde</h3>
                  <p>
                    Conectați la 8 asigurători din România prin Inter Broker.
                    Introduci o singură dată CNP-ul + datele mașinii — primești
                    tot tabloul, ordonat după preț, cu plată simplă în
                    aplicație.
                  </p>
                  <div style={{ marginTop: 24 }}>
                    <Link to="/functii" className="btn btn--secondary">
                      Vezi toate funcțiile <Icon name="arrow_forward" />
                    </Link>
                  </div>
                </div>
                <div className="compare-tile-demo">
                  <div className="compare-tile-demo-head">
                    <strong>Oferte RCA — Dacia Spring</strong>
                    <span className="pill info">8 oferte</span>
                  </div>
                  <div className="offer-row offer-row--best">
                    <div className="offer-row-meta">
                      <strong>Allianz Țiriac</strong>
                      <span>RCA 12 luni</span>
                    </div>
                    <div className="offer-row-price">
                      <div className="price">428 lei</div>
                      <span className="pill success">cel mai ieftin</span>
                    </div>
                  </div>
                  <div className="offer-row">
                    <div className="offer-row-meta">
                      <strong>Groupama</strong>
                      <span>RCA 12 luni</span>
                    </div>
                    <div className="offer-row-price">
                      <div className="price">472 lei</div>
                    </div>
                  </div>
                  <div className="offer-row">
                    <div className="offer-row-meta">
                      <strong>Omniasig VIG</strong>
                      <span>RCA 12 luni</span>
                    </div>
                    <div className="offer-row-price">
                      <div className="price">489 lei</div>
                    </div>
                  </div>
                  <div className="offer-row offer-row--muted">
                    <div className="offer-row-meta">
                      <strong>Generali</strong>
                      <span>RCA 12 luni</span>
                    </div>
                    <div className="offer-row-price">
                      <div className="price">514 lei</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPANION TEASER */}
      <section className="section">
        <div className="shell">
          <div className="indigo-block">
            <div className="indigo-block-grid">
              <div>
                <div className="eyebrow">Nou — Aplicația web</div>
                <h2>
                  La birou, pe browser.{" "}
                  <em style={{ color: "var(--primary-fixed-dim)", fontStyle: "normal" }}>
                    Aceleași date, mai mult spațiu.
                  </em>
                </h2>
                <p>
                  Pentru flotele mici și familiile cu 3+ mașini: vedere de
                  ansamblu pe ecran mare, căutare și filtre, partajare cu
                  mecanicul sau cu șoferul.
                </p>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <Link to="/app" className="btn btn--ghost-light">
                    Vezi aplicația web
                    <Icon name="arrow_forward" />
                  </Link>
                </div>
              </div>
              <div className="indigo-block-pills">
                {[
                  "📊 Tablou flotă",
                  "🔍 Căutare și filtre",
                  "🔔 Reminder mail",
                  "👥 Mai mulți utilizatori",
                  "🗂️ Arhivă PDF",
                ].map((label) => (
                  <span key={label} className="pill">
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="section" id="preturi">
        <div className="shell">
          <div className="free-block">
            <div className="free-eyebrow">Preț</div>
            <h2 className="free-title">
              Gratuit. <em>Fără reclame, fără abonament.</em>
            </h2>
            <p className="free-lede">
              Lansăm aplicația gratuit pentru toată lumea. Nu plătești nimic
              acum, nu îți cerem cardul, nu te încurcăm cu trial-uri. Când vom
              adăuga funcții avansate pentru flote, vei ști dinainte.
            </p>
            <a href="#download" className="btn btn--primary">
              Descarcă gratuit
            </a>
          </div>
        </div>
      </section>

      {/* DOWNLOAD CTA */}
      <section className="section" id="download">
        <div className="shell">
          <div className="cta-strip">
            <div style={{ position: "relative", zIndex: 1 }}>
              <h2>
                Ia-ți asistentul. <em>Liniștit. Acum.</em>
              </h2>
              <p>
                Descarci. Adaugi mașina. Gata. Nu uităm noi când expiră ITP-ul
                tău, ca să poți uita tu.
              </p>
              <div className="store-row">
                <a
                  href="https://play.google.com/store/apps/details?id=asistent.auto&hl=ro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="store"
                >
                  <div className="glyph">
                    <Icon name="android" />
                  </div>
                  <div className="label">
                    <small>Descarcă pe</small>
                    <strong>Google Play</strong>
                  </div>
                </a>
              </div>
            </div>
            <div className="cta-strip-rating">
              <div className="cta-strip-rating-num">4.5★</div>
              <div className="cta-strip-rating-meta">
                Bazat pe 148+ recenzii Google Play.
              </div>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}
